import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { urls } from './config.dev';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClinicMedical, faLock, faSignInAlt, faUserPlus, faEye, faEyeSlash, faSpinner } from '@fortawesome/free-solid-svg-icons';
import JSEncrypt from 'jsencrypt';
import medicalTeamImage1 from './images/LW1.PNG';
import medicalTeamImage2 from './images/LW2.PNG';
import medicalTeamImage3 from './images/LW3.PNG';
import medicalTeamImage4 from './images/LW4.PNG';
import logoImage from './images/LOG3.png';

function Login() {
  const [formData, setFormData] = useState({
    clinicName: '',
    password: '',
    showPassword: false,
  });

  const [loading, setLoading] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [showLogo, setShowLogo] = useState(true);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const navigate = useNavigate();

  const images = [
    medicalTeamImage1,
    medicalTeamImage2,
    medicalTeamImage3,
    medicalTeamImage4,
  ];

  useEffect(() => {
    // Preload logo first
    const loadLogo = () => {
      const img = new Image();
      img.src = logoImage;
      img.onload = () => {
        setLogoLoaded(true);
        // After logo loads, preload other images
        loadImages();
      };
      img.onerror = () => {
        console.error('Failed to load logo');
        setLogoLoaded(true); // Continue even if logo fails
        loadImages();
      };
    };

    // Preload all other images
    const loadImages = async () => {
      const imagePromises = images.map((src) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.src = src;
          img.onload = () => resolve(src);
          img.onerror = () => reject(`Failed to load image: ${src}`);
        });
      });

      try {
        await Promise.all(imagePromises);
        setImagesLoaded(true);
        setCurrentImage(images[0]); // Set the first image after all are loaded
      } catch (error) {
        console.error(error);
        setImagesLoaded(true); // Still proceed even if some images fail to load
      }
    };

    loadLogo();

    const logoTimeout = setTimeout(() => {
      if (logoLoaded) {
        setShowLogo(false);
      }
    }, 3000);

    const imageInterval = setInterval(() => {
      if (imagesLoaded) {
        setCurrentImage(images[Math.floor(Math.random() * images.length)]);
      }
    }, 40000);

    return () => {
      clearTimeout(logoTimeout);
      clearInterval(imageInterval);
    };
  }, [logoLoaded, imagesLoaded]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const togglePasswordVisibility = () => {
    setFormData({ ...formData, showPassword: !formData.showPassword });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.clinicName === '' || formData.password === '') {
      toast.error('Please fill in all fields.', { position: 'top-left' });
      return;
    }

    setLoading(true);
    try {
      const publicKey = `-----BEGIN PUBLIC KEY-----
      MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAup3FU135mAvJT6OheYW3
      pQyWf6jvS4duUMY4cXrlJXyGqu8HqvTU0ewPy6w2HhCPxWboNclkAkPhOCc4URNT
      x1Grg+mCsWmfhVimP2wtfmlBCJ09cyDMYf93iGj8RFf3CshY5yhppT/pX+RgTuXw
      ClpOXe24CLG2VF9suNylk+ReAMLyOxaekYofAMBvvrD4+GYPJgvkTMXCXCKp2PnO
      8+OjiltNMnoyqPEZoXHTV4EXtTrjYnwzSe0WZSSuzgVMhmtdx+IS4eisSumHV1eI
      wBeZwI0bYGxDCedPRassmSFgTFqkkcgIXmEP1n5w/08S/QPr2G+myKTeRqp5RJA5
      PQIDAQAB
      -----END PUBLIC KEY-----`;

      const encrypt = new JSEncrypt();
      encrypt.setPublicKey(publicKey);
      const encryptedPassword = encrypt.encrypt(formData.password);

      const encryptedFormData = { ...formData, password: encryptedPassword, confirmPassword: encryptedPassword };

      const response = await fetch(urls.loginClinic, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(encryptedFormData),
      });

      const responseData = await response.json();

      if (response.ok && responseData.success) {
        const sessionToken = responseData.sessionToken;
        navigate(`/dashboard?token=${sessionToken}`);
        toast.success('Login successful!', { position: 'top-left' });
      } else {
        toast.error('Login failed. Please check your credentials.', { position: 'top-left' });
      }
    } catch (error) {
      console.error('Error logging in:', error);
      toast.error('Login failed!', { position: 'top-left' });
    } finally {
      setLoading(false);
    }
  };

  if (showLogo || !logoLoaded) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f8f9fa'
      }}>
        {logoLoaded ? (
          <img 
            src={logoImage} 
            alt="Logo" 
            style={{
              maxWidth: '300px',
              maxHeight: '300px',
              objectFit: 'contain'
            }} 
          />
        ) : (
          <FontAwesomeIcon 
            icon={faSpinner} 
            spin 
            style={{
              fontSize: '3rem',
              color: '#3498db'
            }} 
          />
        )}
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      overflow: 'hidden'
    }}>
      <ToastContainer />

      {/* Left Side - Login Form */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '2rem',
        minWidth: '400px',
        position: 'relative'
      }}>
        {/* Clinic Pro Header - Centered at top of login side */}
        <div style={{
          position: 'absolute',
          top: '2rem',
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '2rem'
        }}>
          <img 
            src={logoImage} 
            alt="Logo" 
            style={{
              width: '40px',
              height: '40px',
              objectFit: 'contain'
            }} 
          />
          <h1 style={{
            margin: 0,
            fontSize: '1.8rem',
            fontWeight: 'bold',
            color: '#2c3e50',
            background: 'linear-gradient(90deg, #3498db, #2ecc71)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textAlign: 'center'
          }}>CLINIC PRO</h1>
        </div>
        
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          padding: '2rem',
          width: '100%',
          maxWidth: '400px',
          marginTop: '4rem'
        }}>
          <h2 style={{
            textAlign: 'center',
            marginBottom: '1.5rem',
            color: '#2c3e50',
            fontSize: '1.5rem',
            fontWeight: '600'
          }}>Welcome Back</h2>
          
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '500',
                color: '#2c3e50'
              }}>
                <FontAwesomeIcon icon={faClinicMedical} style={{ marginRight: '8px' }} /> 
                Clinic Name
              </label>
              <input 
                type="text" 
                name="clinicName" 
                value={formData.clinicName} 
                onChange={handleChange} 
                required 
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }} 
              />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '500',
                color: '#2c3e50'
              }}>
                <FontAwesomeIcon icon={faLock} style={{ marginRight: '8px' }} /> 
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input 
                  type={formData.showPassword ? 'text' : 'password'} 
                  name="password" 
                  value={formData.password} 
                  onChange={handleChange} 
                  required 
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxSizing: 'border-box',
                    paddingRight: '35px'
                  }} 
                />
                <FontAwesomeIcon 
                  icon={formData.showPassword ? faEyeSlash : faEye} 
                  onClick={togglePasswordVisibility}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    color: '#7f8c8d'
                  }} 
                />
              </div>
            </div>
            <div style={{
              display: 'flex',
              gap: '1rem',
              marginTop: '2rem'
            }}>
              <button 
                type="submit" 
                disabled={loading} 
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? 'Logging in...' : (
                  <>
                    <FontAwesomeIcon icon={faSignInAlt} /> 
                    Log In
                  </>
                )}
              </button>
              <button 
                type="button" 
                onClick={() => navigate('/clinic-registration')} 
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#2ecc71',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <FontAwesomeIcon icon={faUserPlus} /> 
                Register
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Right Side - Image Display */}
      <div style={{
        flex: 1,
        position: 'relative',
        backgroundColor: '#f1f5f9',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minWidth: '300px'
      }}>
        {currentImage && (
          <div style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <img 
              src={currentImage} 
              alt="Medical Team" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center'
              }} 
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default Login;